import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getDepostionReport } from "reduxStore/action/webReportActions";
// import { getReportPropertyList } from "reduxStore/action/reportActions";
// import TableDataNotFound from "component/TableDataNotFound";
import strings from "translate/language";
import { handleDetailResponse, handleListResponse } from "utils/utils";
import { admin_id } from "utils/constants";
import {
  formatPhoneForRole,
  shouldMaskPhoneForUser,
} from "utils/phoneMask";
import Breadcrumb from "component/common/breadcrumb";
import Select from "react-select";
import ExcelJS from "exceljs";
import propertyService from "reduxStore/service/propertyService";
import { errorToast } from "utils/httpClient";
import Pagination from "component/common/Pagination";
import { getMasterList } from "reduxStore/action/masterActions";
// import usePage from "utils/usePage";

const DepositionReport = ({ isAuth = false }) => {
  const dispatch = useDispatch();
  const permissionSelector = useSelector((state) => state.permission);
  const loginSelector = useSelector((state) => state.login);
  const { isAdmin = false } = permissionSelector;
  const roleId = handleDetailResponse(loginSelector?.response?.data)?.role_id;
  const showAllProperties =
    isAdmin || String(roleId || "") === String(admin_id || "");
  const shouldMaskPhone = shouldMaskPhoneForUser({
    permissionResponse: permissionSelector?.response,
    isAdmin,
    roleId,
  });
  const displayPhone = (value) => formatPhoneForRole(value, shouldMaskPhone);
  const [formVaild, setFormVaild] = useState({});
  const [endDateDisable, setEndDateDisable] = useState(true);
  const todayDate = new Date().toISOString().split("T")[0];
  const [form, setForm] = useState({
    property_title: "",
    start_date: todayDate,
    end_date: todayDate,
    client_name: "",
    phone_number: "",
    sourcing_manager: "",
    closing_manager: "",
    lead_source: "",
  });
  const [expandedCell, setExpandedCell] = useState(null); // { rowIndex, colKey, text }

  const [propertyList, setPropertyList] = useState([]);
  const [leadSourceOptions, setLeadSourceOptions] = useState([]);

  const masterDataSelector = useSelector((state) => state?.masterData);

  const MAX_CELL_CHARS = 50;
  /** When false, date columns stay visible but values are not shown (UI + Excel). */
  const SHOW_FOLLOWUP_DATE_VALUES = false;
  const SHOW_REVISIT_DATE_VALUES = false;
  const REMARK_CHAIN_SEP = " -> ";

  const isCmRemarkPart = (part) => {
    const p = String(part || "").trim();
    return (
      p.startsWith("(CM)") ||
      /^re-allocated visitor to new closing manager/i.test(p) ||
      /^assign appointment to new cm/i.test(p) ||
      p.startsWith("[CM Assignment]")
    );
  };

  const renderRemarkChainText = (text) => {
    const raw = text && String(text).trim() ? String(text) : "N/A";
    if (raw === "N/A") return raw;
    if (!raw.includes(REMARK_CHAIN_SEP)) {
      return isCmRemarkPart(raw) ? <strong>{raw}</strong> : raw;
    }

    const parts = raw.split(REMARK_CHAIN_SEP);
    return parts.map((part, index) => (
      <React.Fragment key={`remark-part-${index}`}>
        {index > 0 ? REMARK_CHAIN_SEP : null}
        {isCmRemarkPart(part) ? <strong>{part}</strong> : part}
      </React.Fragment>
    ));
  };

  const renderExpandableCell = (rowIndex, colKey, value) => {
    const text = value && String(value).trim() ? String(value) : "N/A";
    const isRemarkChain = colKey === "last_followup_remark";
    const isLong = text.length > MAX_CELL_CHARS;

    if (!isLong) {
      return (
        <span
          style={{ display: "block", maxHeight: "2.5em", overflow: "hidden" }}
        >
          {isRemarkChain ? renderRemarkChainText(text) : text}
        </span>
      );
    }

    return (
      <span
        style={{ display: "block", maxHeight: "2.5em", overflow: "hidden" }}
      >
        {`${text.slice(0, MAX_CELL_CHARS)}...`}{" "}
        <button
          type="button"
          className="btn btn-link p-0 text-primary"
          style={{ fontSize: "12px", verticalAlign: "baseline" }}
          onClick={(e) => {
            e.stopPropagation();
            setExpandedCell({ rowIndex, colKey, text });
          }}
        >
          See more
        </button>
      </span>
    );
  };
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true); // Loading state
  const [apiError, setApiError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(0); // Current page number (0-indexed)
  const [totalRecords, setTotalRecords] = useState(0); // Total records from API
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
  const tableContainerRef = useRef(null); // Reference for scroll container

  const extractReportRows = (payload) => {
    if (!payload) return { rows: [], total: 0 };
    if (Array.isArray(payload)) {
      return { rows: payload, total: payload.length };
    }
    if (Array.isArray(payload.data)) {
      return {
        rows: payload.data,
        total: payload.total ?? payload.data.length,
      };
    }
    if (payload.data?.records || payload.data?.data) {
      const records = payload.data.records || payload.data.data;
      return {
        rows: records,
        total:
          payload.data.total ?? payload.data.totalCount ?? records.length,
      };
    }
    return { rows: [], total: 0 };
  };

  const applyDepositionPayload = useCallback((payload) => {
    const status = Array.isArray(payload) ? 200 : Number(payload?.status);
    if (status === 200) {
      const { rows, total } = extractReportRows(payload);
      setReportData(rows);
      setTotalRecords(total);
      setLoading(false);
      setApiError(false);
      setErrorMessage("");
      return;
    }

    setReportData([]);
    setTotalRecords(0);
    setLoading(false);
    setApiError(true);
    setErrorMessage(
      (payload && !Array.isArray(payload) && payload.message) ||
        "Failed to fetch data. Please try again.",
    );
  }, []);

  const loadDepositionReport = useCallback(
    async (params) => {
      setLoading(true);
      setApiError(false);
      setErrorMessage("");
      try {
        const payload = await dispatch(getDepostionReport(params));
        applyDepositionPayload(payload);
      } catch {
        setReportData([]);
        setTotalRecords(0);
        setLoading(false);
        setApiError(true);
        setErrorMessage("Failed to fetch data. Please try again.");
      }
    },
    [applyDepositionPayload, dispatch],
  );

  // Backend currently returns the full result set (limit/offset not applied server-side).
  // Slice here so the table only renders the current page; Pagination uses totalRecords.
  const pagedTableRows = useMemo(() => {
    if (!reportData || !Array.isArray(reportData)) return [];
    const start = currentPage * itemsPerPage;
    return reportData.slice(start, start + itemsPerPage);
  }, [reportData, currentPage, itemsPerPage]);

  const columnsForExcel = [
    { header: "Sr.No.", key: "sr_no", width: 10 },
    { header: "Region", key: "region", width: 15 },
    { header: "Visit Type", key: "revisitType", width: 15 },
    { header: "Date", key: "created_date", width: 15 },
    { header: "Client Name", key: "customer_name", width: 20 }, // moved up
    { header: "Property Title", key: "property_name", width: 40 },
    { header: "Mobile", key: "customer_mobile", width: 20 },
    // { header: "Role Title", key: "role_title", width: 20 },
    { header: "Email", key: "customer_email", width: 30 },
    { header: "Resi. Location", key: "customer_location", width: 20 },
    { header: "Sub Location", key: "customer_sub_location", width: 20 },
    { header: "Profession", key: "customer_designation", width: 20 },
    { header: "Work Location", key: "customer_office_address", width: 20 },
    { header: "Ethnicity", key: "ethnicity", width: 20 },
    { header: "Visiting With", key: "visit_with", width: 20 },
    { header: "Visitor Status", key: "qualified", width: 20 },
    { header: "Lead Priority", key: "lead_priority", width: 20 },
    {
      header: "Possession Period",
      key: "expected_possession_period",
      width: 25,
    },
    { header: "Config", key: "configuration", width: 15 },
    { header: "Budget", key: "budget", width: 15 },
    { header: "Purpose", key: "purpose", width: 20 },
    { header: "Lead Source", key: "lead_source", width: 20 },
    { header: "CP Firm Name", key: "cp_firm_name", width: 50 },
    { header: "CP Name", key: "cp_name", width: 50 },
    { header: "CP Contact", key: "cp_contact", width: 20 },
    { header: "RERA Number", key: "rera_number", width: 20 },
    { header: "Referrer Name", key: "referrer_name", width: 30 },
    { header: "Referrer Contact", key: "referrer_contact", width: 20 },
    { header: "Sourcing Manager", key: "sourcing_manager", width: 30 },
    { header: "Closing Manager", key: "closing_manager", width: 30 },
    { header: "Lead Status", key: "lead_status", width: 20 },
    { header: "Walkin - Remark", key: "first_appointment_remarks", width: 60 },
    { header: "Followup Date", key: "followup_dates", width: 45 },
    { header: "Follow-up Remark", key: "last_followup_remark", width: 60 },
    // { header: "NFD", key: "second_followup_date", width: 20 },
    // { header: "STATUS", key: "followup_status", width: 20 },
    { header: "Lost Reason", key: "lost_reason", width: 60 },
    { header: "Lost Remark", key: "lost_remark", width: 60 },
    { header: "Revisit Date", key: "revisit_dates", width: 35 },
  ];

  useEffect(() => {
    // Hide global loader for this page
    const loaderElement = window.document.getElementById("loaderoverlay");
    if (loaderElement) {
      loaderElement.style.display = "none";
    }

    loadDepositionReport({
      limit: itemsPerPage,
      offset: 0,
    });
    dispatch(getMasterList({ type: 13 }));
    // Do not use Redux PROPERTY_LIST here: Property Management (and others) call
    // getAllProperty({ limit: 10 }) into the same slice and would shrink this dropdown to 10 rows.
    let cancelled = false;
    const propertyRequest = showAllProperties
      ? propertyService.getAllProperty({
          skip_metrics: true,
          limit: 10000,
          offset: 0,
        })
      : propertyService.getAgentProperty({ offset: 0, limit: 10000 });

    propertyRequest
      .then((res) => {
        if (cancelled) return;
        if (res?.data?.status === 200) {
          let list = handleListResponse(res.data.data);
          if (!showAllProperties) {
            list = list.filter((item) => item.status === true);
          }
          setPropertyList(list);
        } else {
          setPropertyList([]);
        }
      })
      .catch((e) => {
        if (cancelled) return;
        const { response = strings.initialError } = e;
        errorToast(response?.data?.message);
        setPropertyList([]);
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch, loadDepositionReport, showAllProperties]);

  useEffect(() => {
    if (masterDataSelector?.response?.status === 200) {
      const list = handleListResponse(masterDataSelector.response.data) || [];
      setLeadSourceOptions(
        list
          .map((item) => item?.title)
          .filter(Boolean)
          .map((title) => ({ value: title, label: title })),
      );
    } else {
      setLeadSourceOptions([]);
    }
  }, [masterDataSelector]);

  // Ensure global loader stays hidden throughout component lifecycle
  useEffect(() => {
    const hideGlobalLoader = () => {
      const loaderElement = window.document.getElementById("loaderoverlay");
      if (loaderElement) {
        loaderElement.style.display = "none";
      }
    };

    // Hide immediately
    hideGlobalLoader();

    // Set interval to keep checking and hiding (in case other code tries to show it)
    const intervalId = setInterval(hideGlobalLoader, 100);

    // Cleanup on unmount
    return () => {
      clearInterval(intervalId);
    };
  }, []);

  // If the dataset shrinks, keep the active page in range (e.g. fewer total rows).
  useEffect(() => {
    if (!reportData?.length) return;
    const maxPage = Math.max(
      0,
      Math.ceil(reportData.length / itemsPerPage) - 1,
    );
    setCurrentPage((p) => (p > maxPage ? maxPage : p));
  }, [reportData, itemsPerPage]);

  const handleSelectChange = (selectedOption) => {
    setForm({
      ...form,
      property_title: selectedOption ? selectedOption.value : "",
    });
  };

  const handleLeadSourceSelectChange = (selectedOption) => {
    setForm({
      ...form,
      lead_source: selectedOption ? selectedOption.value : "",
    });
  };

  const handleValid = () => {
    let err = { ...formVaild };
    let formError = false;

    // If dates are provided, both should be there. Otherwise, show an error.
    if (
      (form.start_date && !form.end_date) ||
      (!form.start_date && form.end_date)
    ) {
      formError = true;
      err["date"] = strings.startEndDateErr;
    }

    setFormVaild(err);
    return formError;
  };

  // Page change: client-side only — full data is already in reportData (API returns all rows).
  // When server-side pagination is added, dispatch here with offset: newPage * itemsPerPage.
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(0);
  };

  const filterDetail = () => {
    if (!handleValid()) {
      setCurrentPage(0);
      loadDepositionReport({
        ...form,
        limit: itemsPerPage,
        offset: 0,
      });
    }
  };

  const filterReset = () => {
    setForm({
      property_title: "",
      start_date: todayDate,
      end_date: todayDate,
      client_name: "",
      phone_number: "",
      sourcing_manager: "",
      closing_manager: "",
      lead_source: "",
    });
    setCurrentPage(0);
    loadDepositionReport({
      start_date: todayDate,
      end_date: todayDate,
      limit: itemsPerPage,
      offset: 0,
    });
  };

  const handleInputField = (e) => {
    const nextFormState = {
      ...form,
      [e.target.name]: e.target.value,
    };
    setForm(nextFormState);
    setFormVaild({ ...formVaild, [e.target.name]: "" });
  };

  useEffect(() => {
    if (form.start_date === "") {
      setEndDateDisable(true);
    } else {
      setEndDateDisable(false);
    }
  }, [form.start_date]);

  const formatDate = (date) => {
    if (!date) return "N/A";

    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return "N/A";

    const day = ("0" + d.getDate()).slice(-2);
    const month = ("0" + (d.getMonth() + 1)).slice(-2);
    const year = d.getFullYear();

    return `${day}/${month}/${year}`;
  };

  const formatDateTime = (date) => {
    if (!date) return "N/A";

    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return "N/A";

    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(d);

    const pick = (type) => parts.find((p) => p.type === type)?.value || "";
    return `${pick("day")}/${pick("month")}/${pick("year")} (${pick("hour")}:${pick("minute")})`;
  };

  const getFollowupDatesText = (item) => {
    const dates = Array.isArray(item?.followup_dates)
      ? item.followup_dates
      : [item?.first_followup_date, item?.second_followup_date].filter(Boolean);

    const formatted = dates
      .map((d) => formatDate(d))
      .filter((v) => v && v !== "N/A");

    return formatted.length > 0 ? formatted.join(", ") : "N/A";
  };

  const getRevisitDatesText = (item) => {
    const dates = Array.isArray(item?.revisit_dates)
      ? item.revisit_dates
      : item?.revisit_appointment
        ? [item.revisit_appointment]
        : [];

    const formatted = dates
      .map((d) => formatDate(d))
      .filter((v) => v && v !== "N/A");

    return formatted.length > 0 ? formatted.join(", ") : "N/A";
  };

  const capitalizeWords = (text = "") => {
    if (typeof text !== "string") {
      return "";
    }

    return text
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const exportToExcel = async () => {
    // Note: This will export only the current page data (10 records)
    // To export all data, you would need to call API with limit=totalRecords, offset=0
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Disposition Report");

    const getAppointmentStatus = (status) => {
      switch (status) {
        case 1:
          return "Not Visit";
        case 2:
          return "Revisit";
        case 3:
          return "Booking";
        case 4:
          return "Visit Cancel";
        case 5:
          return "Reschedule";
        case 6:
          return "Not fit for sale";
        case 10:
          return "Follow-up";
        default:
          return "N/A";
      }
    };

    if (!form.property_title) {
      // worksheet.columns = [
      //   { header: "Sr.No.", key: "sr_no", width: 10 },
      //   { header: "Region", key: "region", width: 15 },
      //   { header: "Visit Type", key: "revisitType", width: 15 },
      //   { header: "Date", key: "created_date", width: 15 },
      //   { header: "Client Name", key: "customer_name", width: 20 },
      //   { header: "Property Title", key: "property_name", width: 40 },
      //   { header: "Mobile", key: "customer_mobile", width: 20 },
      //   // { header: "Role Title", key: "role_title", width: 20 },
      //   { header: "Email", key: "customer_email", width: 30 },
      //   { header: "Resi. Location", key: "customer_location", width: 20 },
      //   { header: "Profession", key: "customer_designation", width: 20 },
      //   { header: "Work Location", key: "customer_office_address", width: 20 },
      //   { header: "Ethnicity", key: "ethnicity", width: 20 },
      //   { header: "Visiting With", key: "visit_with", width: 20 },
      //   { header: "Visitor Status", key: "qualified", width: 20 },
      //   { header: "Lead Priority", key: "lead_priority", width: 20 },
      //   {
      //     header: "Possession Period",
      //     key: "expected_possession_period",
      //     width: 25,
      //   },
      //   { header: "Config", key: "configuration", width: 15 },
      //   { header: "Budget", key: "budget", width: 15 },
      //   { header: "Purpose", key: "purpose", width: 20 },
      //   { header: "Lead Source", key: "lead_source", width: 20 },
      //   { header: "CP Firm Name", key: "cp_firm_name", width: 50 },
      //   { header: "CP Name", key: "cp_name", width: 50 },
      //   { header: "CP Contact", key: "cp_contact", width: 20 },
      //   { header: "RERA Number", key: "rera_number", width: 20 },
      //   { header: "Referrer Name", key: "referrer_name", width: 30 },
      //   { header: "Referrer Contact", key: "referrer_contact", width: 20 },
      //   { header: "Sourcing Manager", key: "sourcing_manager", width: 30 },
      //   { header: "Closing Manager", key: "closing_manager", width: 30 },
      //   { header: "Lead Status", key: "lead_status", width: 20 },
      //   {
      //     header: "Walkin - Remark",
      //     key: "first_appointment_remarks",
      //     width: 60,
      //   },
      //   { header: "1st Follow-Up Date", key: "first_followup_date", width: 30 },
      //   { header: "Follow-up Remark", key: "last_followup_remark", width: 60 },
      //   // { header: "NFD", key: "second_followup_date", width: 20 },
      //   // { header: 'STATUS', key: 'followup_status', width: 20 },
      //   { header: "Lost Reason", key: "lost_reason", width: 60 },
      //   { header: "Lost Remark", key: "lost_remark", width: 60 },
      //   { header: "Revisit Date", key: "revisit_appointment", width: 20 },
      // ];
      worksheet.columns = columnsForExcel;

      worksheet.getRow(1).eachCell((cell) => {
        cell.font = {
          bold: true,
          size: 14,
          color: { argb: "FFFFFF" },
        };
        cell.alignment = { vertical: "middle", horizontal: "center" };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "4F81BD" },
        };
        cell.border = {
          top: { style: "thin", color: { argb: "000000" } },
          left: { style: "thin", color: { argb: "000000" } },
          bottom: { style: "thin", color: { argb: "000000" } },
          right: { style: "thin", color: { argb: "000000" } },
        };
      });

      reportData.forEach((item, index) => {
        const createdDate = item.createddate
          ? formatDate(item.createddate)
          : "N/A";
        worksheet.addRow({
          sr_no: index + 1,
          revisitType: item.revisitType,
          region: item.region || "N/A",
          created_date: createdDate,
          customer_name: item.customer_name || "N/A",
          property_name: item.property_name || "N/A",
          customer_mobile: displayPhone(item.customer_mobile),
          customer_email: item.customer_email || "N/A",
          // role_title: item.role_title || "N/A",
          customer_location: item.customer_location || "N/A",
          customer_designation: item.customer_designation || "N/A",
          customer_office_address: item.customer_office_address || "N/A",
          ethnicity: item.ethnicity || "N/A",
          visit_with: capitalizeWords(item.visit_with) || "N/A",
          qualified:
            item.qualified === true
              ? "Qualified"
              : item.qualified === false
                ? "Unqualified"
                : "N/A",
          lead_priority: capitalizeWords(item.lead_priority) || "N/A",
          expected_possession_period: item.expected_possession_period
            ? item.expected_possession_period
            : item.expected_possession_date
              ? formatDate(item.expected_possession_date)
              : "N/A",
          configuration: item.configuration || "N/A",
          budget: item.max_budget || "N/A",
          purpose: item.purpose || "N/A",
          lead_source:
            item.referrel_partner === 1
              ? "Referral Partner"
              : item.lead_source_title || "N/A",
          cp_firm_name: item.cp_firm_name || "N/A",
          cp_name: item.cp_name || "N/A",
          cp_contact: item.cp_contact || "N/A",
          rera_number: item.rera_certificate_no || "N/A",
          referrer_name: item.referrer_name || "N/A",
          referrer_contact: item.referrer_contact || "N/A",
          sourcing_manager: item.sourcing_manager || "N/A",
          closing_manager: item.closing_manager || "N/A",
          lead_status:
            getAppointmentStatus(item.first_appointment_status) || "N/A",
          first_appointment_remarks: item.first_appointment_remarks || "N/A",
          followup_dates: SHOW_FOLLOWUP_DATE_VALUES
            ? getFollowupDatesText(item)
            : "N/A",
          last_followup_remark: item.last_followup_remark || "N/A",
          // second_followup_date: secondFollowupDate,
          // followup_status: item.followup_status || 'N/A',
          lost_reason: item.lost_reason_status_6 || "N/A",
          lost_remark: item.remarks_status_6 || "N/A",
          revisit_dates: SHOW_REVISIT_DATE_VALUES
            ? getRevisitDatesText(item)
            : "N/A",
        });
      });
    } else {
      // worksheet.columns = [
      //   { header: "Sr.No.", key: "sr_no", width: 10 },
      //   { header: "Region", key: "region", width: 15 },
      //   { header: "Visit Type", key: "revisitType", width: 15 },
      //   { header: "Date", key: "created_date", width: 15 },
      //   { header: "Property Title", key: "property_name", width: 40 },
      //   { header: "Client Name", key: "customer_name", width: 20 },
      //   { header: "Mobile", key: "customer_mobile", width: 20 },
      //   // { header: "Role Title", key: "role_title", width: 20 },
      //   { header: "Email", key: "customer_email", width: 30 },
      //   { header: "Resi. Location", key: "customer_location", width: 20 },
      //   { header: "Profession", key: "customer_designation", width: 20 },
      //   { header: "Work Location", key: "customer_office_address", width: 20 },
      //   { header: "Ethnicity", key: "ethnicity", width: 20 },
      //   { header: "Visiting With", key: "visit_with", width: 20 },
      //   { header: "Visitor Status", key: "qualified", width: 20 },
      //   { header: "Lead Priority", key: "lead_priority", width: 20 },
      //   {
      //     header: "Possession Period",
      //     key: "expected_possession_period",
      //     width: 25,
      //   },
      //   { header: "Config", key: "configuration", width: 15 },
      //   { header: "Budget", key: "budget", width: 15 },
      //   { header: "Purpose", key: "purpose", width: 20 },
      //   { header: "Lead Source", key: "lead_source", width: 20 },
      //   { header: "CP Firm Name", key: "cp_firm_name", width: 50 },
      //   { header: "CP Name", key: "cp_name", width: 50 },
      //   { header: "CP Contact", key: "cp_contact", width: 20 },
      //   { header: "RERA Number", key: "rera_number", width: 20 },
      //   { header: "Referrer Name", key: "referrer_name", width: 30 },
      //   { header: "Referrer Contact", key: "referrer_contact", width: 20 },
      //   { header: "Sourcing Manager", key: "sourcing_manager", width: 30 },
      //   { header: "Closing Manager", key: "closing_manager", width: 30 },
      //   { header: "Lead Status", key: "lead_status", width: 20 },
      //   {
      //     header: "Walkin - Remark",
      //     key: "first_appointment_remarks",
      //     width: 60,
      //   },
      //   { header: "1st Follow-Up Date", key: "first_followup_date", width: 30 },
      //   { header: "Follow-up Remark", key: "last_followup_remark", width: 60 },
      //   // { header: "NFD", key: "second_followup_date", width: 20 },
      //   // { header: 'STATUS', key: 'followup_status', width: 20 },
      //   { header: "Lost Reason", key: "lost_reason", width: 60 },
      //   { header: "Lost Remark", key: "lost_remark", width: 60 },
      //   { header: "Revisit Date", key: "revisit_appointment", width: 20 },
      // ];
      worksheet.columns = columnsForExcel;
      worksheet.getRow(1).eachCell((cell) => {
        cell.font = {
          bold: true,
          size: 14,
          color: { argb: "FFFFFF" },
        };
        cell.alignment = { vertical: "middle", horizontal: "center" };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "4F81BD" },
        };
        cell.border = {
          top: { style: "thin", color: { argb: "000000" } },
          left: { style: "thin", color: { argb: "000000" } },
          bottom: { style: "thin", color: { argb: "000000" } },
          right: { style: "thin", color: { argb: "000000" } },
        };
      });

      reportData.forEach((item, index) => {
        const createdDate = item.createddate
          ? formatDate(item.createddate)
          : "N/A";
        worksheet.addRow({
          sr_no: index + 1,
          revisitType: item.revisitType,
          region: item.region || "N/A",
          created_date: createdDate,
          property_name: item.property_name || "N/A",
          customer_name: item.customer_name || "N/A",
          customer_mobile: displayPhone(item.customer_mobile),
          // role_title: item.role_title || "N/A",
          customer_email: item.customer_email || "N/A",
          customer_location: item.customer_location || "N/A",
          customer_designation: item.customer_designation || "N/A",
          customer_office_address: item.customer_office_address || "N/A",
          ethnicity: item.ethnicity || "N/A",
          visit_with: capitalizeWords(item.visit_with) || "N/A",
          qualified:
            item.qualified === true
              ? "Qualified"
              : item.qualified === false
                ? "Unqualified"
                : "N/A",
          lead_priority: capitalizeWords(item.lead_priority) || "N/A",
          expected_possession_period: item.expected_possession_period
            ? item.expected_possession_period
            : item.expected_possession_date
              ? formatDate(item.expected_possession_date)
              : "N/A",
          configuration: item.configuration || "N/A",
          budget: item.max_budget || "N/A",
          purpose: item.purpose || "N/A",
          lead_source:
            item.referrel_partner === 1
              ? "Referral Partner"
              : item.lead_source_title || "N/A",
          cp_firm_name: item.cp_firm_name || "N/A",
          cp_name: item.cp_name || "N/A",
          cp_contact: item.cp_contact || "N/A",
          rera_number: item.rera_certificate_no || "N/A",
          referrer_name: item.referrer_name || "N/A",
          referrer_contact: item.referrer_contact || "N/A",
          sourcing_manager: item.sourcing_manager || "N/A",
          closing_manager: item.closing_manager || "N/A",
          lead_status:
            getAppointmentStatus(item.first_appointment_status) || "N/A",
          first_appointment_remarks: item.first_appointment_remarks || "N/A",
          followup_dates: SHOW_FOLLOWUP_DATE_VALUES
            ? getFollowupDatesText(item)
            : "N/A",
          last_followup_remark: item.last_followup_remark || "N/A",
          // second_followup_date: secondFollowupDate,
          // followup_status: item.followup_status || 'N/A',
          lost_reason: item.lost_reason_status_6 || "N/A",
          lost_remark: item.remarks_status_6 || "N/A",
          revisit_dates: SHOW_REVISIT_DATE_VALUES
            ? getRevisitDatesText(item)
            : "N/A",
        });
      });
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/octet-stream" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "disposition_report.xlsx";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="page-body">
      {isAuth && (
        <Breadcrumb
          title={strings.depositionReport}
          parentListing={null}
          titleactive={strings.depositionReport}
        />
      )}
      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-body">
              <form className="theme-form">
                <div className="row">
                  <div className="col-md-4">
                    <span className="cardText">{strings.startDate}</span>
                    <div
                      className="input-group date"
                      id="dt-date"
                      data-target-input="nearest"
                    >
                      <input
                        className="form-control datetimepicker-input digits"
                        type="date"
                        data-target="#dt-date"
                        name="start_date"
                        value={form.start_date}
                        onChange={handleInputField}
                        // min={form.start_date}
                      />
                    </div>
                    {formVaild.start_date && (
                      <span style={{ color: "red" }}>
                        {formVaild.start_date}
                      </span>
                    )}
                  </div>
                  <div className="col-md-4">
                    <span className="cardText">{strings.endDate}</span>
                    <div
                      className="input-group date"
                      id="dt-date"
                      data-target-input="nearest"
                    >
                      <input
                        className="form-control datetimepicker-input digits"
                        type="date"
                        name="end_date"
                        value={form.end_date}
                        onChange={handleInputField}
                        min={form.start_date}
                        // max={form.end_date}
                        disabled={endDateDisable}
                      />
                    </div>
                    {formVaild.end_date && (
                      <span style={{ color: "red" }}>{formVaild.end_date}</span>
                    )}
                  </div>
                  <div className="col-md-4">
                    <span className="cardText">{strings.propertyName}</span>

                    <Select
                      isDisabled={propertyList.length === 0}
                      className="basic-single"
                      classNamePrefix="select"
                      isSearchable
                      isClearable
                      options={propertyList.map((property) => ({
                        value: property.property_title,
                        label: property.property_title,
                      }))}
                      value={
                        form.property_title
                          ? {
                              label: form.property_title,
                              value: form.property_title,
                            }
                          : null
                      }
                      onChange={handleSelectChange}
                      placeholder="Select property"
                    />
                  </div>
                </div>

                <div className="row pt-3">
                  <div className="col-md-4">
                    <span className="cardText">{strings.customerName}</span>
                    <input
                      className="form-control digits"
                      type="text"
                      autoComplete="off"
                      name="client_name"
                      placeholder={strings.searchVisitorName}
                      value={form.client_name}
                      onChange={handleInputField}
                    />
                  </div>
                  <div className="col-md-4">
                    <span className="cardText">
                      {strings.searchByMobileNumber}
                    </span>
                    <input
                      className="form-control digits"
                      type="text"
                      autoComplete="off"
                      name="phone_number"
                      placeholder={strings.searchMobileNumber}
                      value={form.phone_number}
                      onChange={handleInputField}
                      maxLength="10"
                    />
                  </div>
                  <div className="col-md-4">
                    <span className="cardText">{strings.leadSource}</span>
                    <Select
                      className="basic-single"
                      classNamePrefix="select"
                      isSearchable
                      isClearable
                      options={leadSourceOptions}
                      value={
                        form.lead_source
                          ? {
                              label: form.lead_source,
                              value: form.lead_source,
                            }
                          : null
                      }
                      onChange={handleLeadSourceSelectChange}
                      placeholder={strings.leadSource}
                    />
                  </div>
                </div>

                <div className="row pt-3">
                  <div className="col-md-4">
                    <span className="cardText">{strings.sourcingManager}</span>
                    <input
                      className="form-control digits"
                      type="text"
                      autoComplete="off"
                      name="sourcing_manager"
                      placeholder={strings.sourcingManager}
                      value={form.sourcing_manager}
                      onChange={handleInputField}
                    />
                  </div>
                  <div className="col-md-4">
                    <span className="cardText">{strings.closingManager}</span>
                    <input
                      className="form-control digits"
                      type="text"
                      autoComplete="off"
                      name="closing_manager"
                      placeholder={strings.closingManager}
                      value={form.closing_manager}
                      onChange={handleInputField}
                    />
                  </div>
                </div>

                <div className="row pt-3">
                  <div className="col-md-12 pt-4">
                    <div className="form-group d-flex justify-content-end">
                      <button
                        className="btn btn-warning "
                        type="button"
                        onClick={filterDetail}
                      >
                        {strings.submit}
                      </button>
                      <button
                        className="btn btn-dark ml-2"
                        type="button"
                        onClick={filterReset}
                      >
                        {strings.reset}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>

          <hr />
          {reportData && reportData.length > 0 && (
            <div className="d-flex  mb-4 justify-content-end">
              <button className="btn btn-primary" onClick={exportToExcel}>
                Export to Excel
              </button>
            </div>
          )}

          <div className="card">
            <div className="card-body">
              <div ref={tableContainerRef}>
                {apiError ? (
                  <div style={{ padding: "40px 20px", textAlign: "center" }}>
                    <div
                      style={{
                        fontSize: "48px",
                        color: "#dc3545",
                        marginBottom: "20px",
                      }}
                    >
                      <i className="fa fa-exclamation-circle"></i>
                    </div>
                    <h5 style={{ color: "#dc3545", marginBottom: "10px" }}>
                      Error Loading Data
                    </h5>
                    <p style={{ color: "#666", marginBottom: "20px" }}>
                      {errorMessage}
                    </p>
                    {/* <button 
                      className="btn btn-primary" 
                      onClick={() => {
                        setApiError(false);
                        setLoading(true);
                        dispatch(getDepostionReport({ ...form }));
                      }}
                    >
                      Try Again
                    </button> */}
                  </div>
                ) : !loading && reportData && reportData.length === 0 ? (
                  <div style={{ padding: "20px", textAlign: "center" }}>
                    No data available.
                  </div>
                ) : (
                  <div>
                    <table
                      className="table table-striped table-bordered table-sm table-responsive"
                      style={{
                        overflowY: "auto",
                        overflowX: "auto",
                        maxHeight: "500px",
                        height: "500px",
                        position: "relative",
                        tableLayout: "fixed",
                      }}
                    >
                      <thead
                        style={{
                          backgroundColor: "#ffffff",
                          color: "#000",
                          position: "sticky",
                          top: "0",
                          zIndex: 1,
                        }}
                      >
                        <tr>
                          <th style={{ minWidth: "30px" }}>Sr.No.</th>
                          <th style={{ minWidth: "100px" }}>Region</th>
                          <th style={{ minWidth: "150px" }}>Visit Type</th>
                          <th style={{ minWidth: "100px" }}>Date</th>
                          <th style={{ minWidth: "200px" }}>Client Name</th>
                          <th style={{ minWidth: "200px" }}>Property Title</th>
                          <th style={{ minWidth: "150px" }}>Mobile</th>
                          {/* <th style={{ minWidth: "150px" }}>Role Title</th> */}
                          <th style={{ minWidth: "200px" }}>Email</th>
                          <th style={{ minWidth: "200px" }}>Resi. Location</th>
                          <th style={{ minWidth: "200px" }}>Profession</th>
                          <th style={{ minWidth: "200px" }}>Work Location</th>
                          <th style={{ minWidth: "200px" }}>Ethnicity</th>
                          <th style={{ minWidth: "200px" }}>Visiting With</th>
                          <th style={{ minWidth: "200px" }}>Visitor Status</th>
                          <th style={{ minWidth: "200px" }}>Lead Priority</th>
                          <th style={{ minWidth: "200px" }}>
                            Possession Period
                          </th>
                          <th style={{ minWidth: "200px" }}>Config</th>
                          <th style={{ minWidth: "200px" }}>Budget</th>
                          <th style={{ minWidth: "200px" }}>Purpose</th>
                          <th style={{ minWidth: "200px" }}>Lead Source</th>
                          <th style={{ minWidth: "200px" }}>CP Firm Name</th>
                          <th style={{ minWidth: "200px" }}>CP Name</th>
                          <th style={{ minWidth: "200px" }}>CP Contact</th>
                          <th style={{ minWidth: "200px" }}>RERA Number</th>
                          <th style={{ minWidth: "200px" }}>Referrer Name</th>
                          <th style={{ minWidth: "200px" }}>
                            Referrer Contact
                          </th>
                          <th style={{ minWidth: "200px" }}>
                            Sourcing Manager
                          </th>
                          <th style={{ minWidth: "200px" }}>Closing Manager</th>
                          <th style={{ minWidth: "200px" }}>Lead Status</th>
                          <th style={{ minWidth: "300px" }}>Walkin - Remark</th>
                          <th style={{ minWidth: "200px" }}>Followup Date</th>
                          <th style={{ minWidth: "300px" }}>
                            Follow-up / Revisit Remark
                          </th>
                          {/* <th style={{ minWidth: "200px" }}>NFD</th> */}
                          <th style={{ minWidth: "300px" }}>Lost Reason</th>
                          <th style={{ minWidth: "300px" }}>Lost Remark</th>
                          <th style={{ minWidth: "200px" }}>Revisit Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedTableRows.map((item, index) => {
                          const rowIndex = currentPage * itemsPerPage + index;
                          return (
                            <tr key={item._id}>
                              <td>{rowIndex + 1}</td>
                              <td>{item.region || "N/A"}</td>
                              <td>{item.revisitType || "N/A"}</td>
                              <td>
                                {item.createddate
                                  ? formatDate(item.createddate)
                                  : "N/A"}
                              </td>
                              <td
                                style={{
                                  maxWidth: "200px",
                                  maxHeight: "2.5em",
                                  overflow: "hidden",
                                }}
                              >
                                {renderExpandableCell(
                                  rowIndex,
                                  "customer_name",
                                  item.customer_name,
                                )}
                              </td>
                              <td
                                style={{
                                  maxWidth: "240px",
                                  maxHeight: "2.5em",
                                  overflow: "hidden",
                                }}
                              >
                                {renderExpandableCell(
                                  rowIndex,
                                  "property_name",
                                  item.property_name,
                                )}
                              </td>
                              <td>{displayPhone(item.customer_mobile)}</td>
                              {/* <td>{item.role_title || "N/A"}</td> */}
                              <td
                                style={{
                                  maxWidth: "200px",
                                  maxHeight: "2.5em",
                                  overflow: "hidden",
                                }}
                              >
                                {renderExpandableCell(
                                  rowIndex,
                                  "customer_email",
                                  item.customer_email,
                                )}
                              </td>
                              <td
                                style={{
                                  maxWidth: "200px",
                                  maxHeight: "2.5em",
                                  overflow: "hidden",
                                }}
                              >
                                {renderExpandableCell(
                                  rowIndex,
                                  "customer_location",
                                  capitalizeWords(item.customer_location) ||
                                    "N/A",
                                )}
                              </td>
                              <td
                                style={{
                                  maxWidth: "200px",
                                  maxHeight: "2.5em",
                                  overflow: "hidden",
                                }}
                              >
                                {renderExpandableCell(
                                  rowIndex,
                                  "customer_designation",
                                  item.customer_designation,
                                )}
                              </td>
                              <td
                                style={{
                                  maxWidth: "200px",
                                  maxHeight: "2.5em",
                                  overflow: "hidden",
                                }}
                              >
                                {renderExpandableCell(
                                  rowIndex,
                                  "customer_office_address",
                                  capitalizeWords(
                                    item.customer_office_address,
                                  ) || "N/A",
                                )}
                              </td>
                              <td>{item.ethnicity || "N/A"}</td>
                              <td>
                                {capitalizeWords(item.visit_with) || "N/A"}
                              </td>
                              <td>
                                {item.qualified === true
                                  ? strings.qualified
                                  : item.qualified === false
                                    ? strings.unQualified
                                    : "N/A"}
                              </td>
                              <td>
                                {capitalizeWords(item.lead_priority) || "N/A"}
                              </td>
                              <td>
                                {item.expected_possession_period
                                  ? item.expected_possession_period
                                  : item.expected_possession_date
                                    ? formatDate(item.expected_possession_date)
                                    : "N/A"}
                              </td>
                              <td>{item.configuration || "N/A"}</td>
                              <td>{item.max_budget}</td>
                              <td>{item.purpose || "N/A"}</td>
                              <td>
                                {item.referrel_partner === 1
                                  ? "Referral Partner"
                                  : item.lead_source_title || "N/A"}
                              </td>
                              <td
                                style={{
                                  maxWidth: "200px",
                                  maxHeight: "2.5em",
                                  overflow: "hidden",
                                }}
                              >
                                {renderExpandableCell(
                                  rowIndex,
                                  "cp_firm_name",
                                  item.cp_firm_name,
                                )}
                              </td>
                              <td
                                style={{
                                  maxWidth: "200px",
                                  maxHeight: "2.5em",
                                  overflow: "hidden",
                                }}
                              >
                                {renderExpandableCell(
                                  rowIndex,
                                  "cp_name",
                                  item.cp_name,
                                )}
                              </td>
                              <td>{item.cp_contact || "N/A"}</td>
                              <td>{item.rera_certificate_no || "N/A"}</td>
                              <td
                                style={{
                                  maxWidth: "200px",
                                  maxHeight: "2.5em",
                                  overflow: "hidden",
                                }}
                              >
                                {renderExpandableCell(
                                  rowIndex,
                                  "referrer_name",
                                  capitalizeWords(item.referrer_name) || "N/A",
                                )}
                              </td>
                              <td>{item.referrer_contact || "N/A"}</td>
                              <td>{item.sourcing_manager || "N/A"}</td>
                              <td>{item.closing_manager || "N/A"}</td>
                              <td>
                                {item.first_appointment_status === 1
                                  ? "Not Visit"
                                  : item.first_appointment_status === 2
                                    ? "Revisit"
                                    : item.first_appointment_status === 3
                                      ? "Booking"
                                      : item.first_appointment_status === 4
                                        ? "Visit Cancel"
                                        : item.first_appointment_status === 5
                                          ? "Reschedule"
                                          : item.first_appointment_status === 6
                                            ? "Not fit for sale"
                                            : item.first_appointment_status ===
                                                10
                                              ? "Follow-up"
                                              : "N/A"}
                              </td>
                              <td
                                style={{
                                  maxWidth: "300px",
                                  maxHeight: "2.5em",
                                  overflow: "hidden",
                                }}
                              >
                                {renderExpandableCell(
                                  rowIndex,
                                  "first_appointment_remarks",
                                  item.first_appointment_remarks,
                                )}
                              </td>
                              <td>
                                {SHOW_FOLLOWUP_DATE_VALUES
                                  ? renderExpandableCell(
                                      rowIndex,
                                      "followup_dates",
                                      getFollowupDatesText(item),
                                    )
                                  : "N/A"}
                              </td>
                              <td
                                style={{
                                  maxWidth: "300px",
                                  maxHeight: "2.5em",
                                  overflow: "hidden",
                                }}
                              >
                                {renderExpandableCell(
                                  rowIndex,
                                  "last_followup_remark",
                                  item.last_followup_remark,
                                )}
                              </td>
                              {/* <td>
                              {item.second_followup_date
                                ? formatDate(item.second_followup_date)
                                : "N/A"}
                            </td> */}
                              <td
                                style={{
                                  maxWidth: "300px",
                                  maxHeight: "2.5em",
                                  overflow: "hidden",
                                }}
                              >
                                {renderExpandableCell(
                                  rowIndex,
                                  "lost_reason_status_6",
                                  item.lost_reason_status_6,
                                )}
                              </td>
                              <td
                                style={{
                                  maxWidth: "300px",
                                  maxHeight: "2.5em",
                                  overflow: "hidden",
                                }}
                              >
                                {renderExpandableCell(
                                  rowIndex,
                                  "remarks_status_6",
                                  item.remarks_status_6,
                                )}
                              </td>
                              <td>
                                {SHOW_REVISIT_DATE_VALUES
                                  ? renderExpandableCell(
                                      rowIndex,
                                      "revisit_dates",
                                      getRevisitDatesText(item),
                                    )
                                  : "N/A"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {/* {isLoadingMore && (
                      <div
                        style={{
                          padding: "20px",
                          textAlign: "center",
                          backgroundColor: "#fff",
                          borderTop: "1px solid #dee2e6",
                        }}
                      >
                        <div
                          className="spinner-border text-primary"
                          role="status"
                          style={{ width: "2rem", height: "2rem" }}
                        >
                          <span className="sr-only">Loading more...</span>
                        </div>
                        <p style={{ margin: "10px 0 0 0", fontWeight: "500" }}>
                          Loading more records...
                        </p>
                      </div>
                    )} */}
                    {/* {reportData &&
                      visibleRecords < reportData.length &&
                      !isLoadingMore && (
                        <div
                          style={{
                            padding: "15px",
                            textAlign: "center",
                            color: "#007bff",
                            backgroundColor: "#e7f3ff",
                            borderTop: "1px solid #dee2e6",
                            cursor: "pointer",
                          }}
                        >
                          <i className="fa fa-arrow-down"></i> Scroll down to
                          load more ({reportData.length - visibleRecords}{" "}
                          remaining)
                        </div>
                      )}
                    {reportData &&
                      visibleRecords >= reportData.length &&
                      reportData.length > 10 && (
                        <div
                          style={{
                            padding: "15px",
                            textAlign: "center",
                            color: "#28a745",
                            backgroundColor: "#d4edda",
                            borderTop: "1px solid #dee2e6",
                          }}
                        >
                          <i className="fa fa-check-circle"></i> All{" "}
                          {reportData.length} records loaded
                        </div>
                      )} */}

                    {totalRecords > 0 && (
                      <>
                        <div
                          className="d-flex flex-wrap align-items-center px-3 py-2"
                        >
                          <div className="d-flex align-items-center">
                            <label
                              htmlFor="deposition-page-size"
                              className="mb-0 mr-2 text-muted"
                              style={{ fontSize: "14px", whiteSpace: "nowrap" }}
                            >
                              Rows per page
                            </label>
                            <select
                              id="deposition-page-size"
                              className="form-control form-control-sm"
                              style={{ width: "80px" }}
                              value={itemsPerPage}
                              onChange={handlePageSizeChange}
                            >
                              {PAGE_SIZE_OPTIONS.map((size) => (
                                <option key={size} value={size}>
                                  {size}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <Pagination
                          itemsPerPage={itemsPerPage}
                          totalCount={totalRecords}
                          listData={reportData || []}
                          onPageChange={handlePageChange}
                          itemOffset={currentPage}
                          setItemOffset={setCurrentPage}
                        />
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* See more / See less overlay - keeps row height fixed */}
      {expandedCell && (
        <div
          className="modal show"
          style={{
            display: "block",
            backgroundColor: "rgba(0, 0, 0, 0.4)",
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1050,
          }}
          onClick={() => setExpandedCell(null)}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            style={{ maxWidth: "500px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header py-2">
                <h6 className="modal-title">Full content</h6>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => setExpandedCell(null)}
                />
              </div>
              <div
                className="modal-body py-3"
                style={{ maxHeight: "60vh", overflowY: "auto" }}
              >
                <p
                  className="mb-0"
                  style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                >
                  {expandedCell.colKey === "last_followup_remark"
                    ? renderRemarkChainText(expandedCell.text)
                    : expandedCell.text}
                </p>
              </div>
              <div className="modal-footer py-2">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setExpandedCell(null)}
                >
                  See less
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading Modal Popup */}
      {loading && (
        <div
          className="modal show"
          style={{
            display: "block",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
          }}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "100vh",
            }}
          >
            <div className="modal-content" style={{ maxWidth: "400px" }}>
              <div
                className="modal-body text-center"
                style={{ padding: "40px 20px" }}
              >
                <div
                  className="spinner-border text-primary mb-3"
                  role="status"
                  style={{ width: "3rem", height: "3rem" }}
                >
                  <span className="sr-only">Loading...</span>
                </div>
                <h5 className="mb-2">Loading Data</h5>
                <p className="text-muted mb-0">
                  Please wait while we fetch the report data...
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepositionReport;
